import React, { Component, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import listAnalytics from './listAnalytics';
import Button from '../../components/Elements/Button/Button';
import TemplateParser from '../../components/Templates/TemplateParser';
import { updatePagination } from '../../actions/environment';
import { arraysEqual, differenceLeft, difference } from '../../utils/array';
import { addClass, removeClass, addEvent, removeEvent, offset, scrollTo, height } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { deepClone } from '../../utils/object';
import { urlify, rootUrl } from '../../utils/string';
import s from './Pagination.scss';

class Pagination extends Component {
	constructor(props) {
		super(props);

		this.itemsSeen = [];
		this.indexes = {};
		this.itemsSent = [];
		this.paginationContainer = React.createRef();

		this.state = {
			expandedDirection: 'left',
			minorSectionExpanded: [],
			productItemExpanded: [],
		};
	}

	componentDidMount() {
		const { hash = '' } = this.props.payload;
		const section = hash.slice(1) ? document.getElementById(hash.slice(1)) : false;
		let debounced = false;

		if (section) this.scrollToHash(section, hash);

		addEvent(window, 'scroll.pagination', () => {
			window.requestAnimationFrame(() => {
				if (!debounced && this.paginationContainer.current) {
					const paginationTop = offset(this.paginationContainer.current);
					const paginationHeight = height(this.paginationContainer.current);

					// Loads in when one window away.
					if (window.pageYOffset + (height(window) * 2) > paginationTop.top + paginationHeight) {
						this.handleLoadMore(true);
						debounced = true;
						window.setTimeout(() => {
							debounced = false;
						}, 500);
					}
				}
			});
		});

		this.handleListAnalytics();
	}

	componentDidUpdate(prevProps) {
		if (this.props.currentPath !== prevProps.currentPath) {
			this.itemsSeen = [];
			this.itemsSent = [];
			this.indexes = {};
		}

		if (!arraysEqual(this.props.list, prevProps.list)) {
			this.handleListAnalytics();
		}
	}

	componentWillUnmount() {
		removeEvent(window, 'scroll.pagination');
	}

	handleListAnalytics = () => {
		if (this.paginationContainer.current !== null) {
			const options = {
				root: null,
				rootMargin: '0px',
				threshold: [0, 0.5, 1],
			};

			const handleIntersect = (elements) => {
				elements.forEach((el) => {
					if (el.isIntersecting) {
						const id = el.target.dataset.productId;
						this.indexes[id] = el.target.dataset.productIndex;
						if (this.itemsSeen.indexOf(id) === -1) this.itemsSeen.push(id);
						if (el.intersectionRatio < 0.1) removeClass(el.target, 'u_active');
						if (el.intersectionRatio > 0.9) addClass(el.target, 'u_active');
					}
				});
				const itemsToSend = differenceLeft(this.itemsSeen, this.itemsSent);
				if (itemsToSend.length > 0) {
					listAnalytics(itemsToSend.map((i) => this.props.cache[i]), itemsToSend.map((i) => this.indexes[i]), this.props.setup, this.props.currentPath || 'productPicker');
					this.itemsSent = [...this.itemsSent, ...itemsToSend];
				}
			};

			const observer = new window.IntersectionObserver(handleIntersect, options);
			this.paginationContainer.current.querySelectorAll('.u_productItem').forEach((e) => observer.observe(e));
		}
	}

	scrollToHash = (el, hash) => {
		let imagesLoaded = null;

		const allLoaded = () => {
			if (this.paginationContainer !== undefined) {
				const imgs = this.paginationContainer.current.querySelectorAll('img');
				imagesLoaded = [...imgs].length;

				[...imgs].forEach((img) => {
					if (img.complete) imagesLoaded -= 1;
				});
			}

			if (this.paginationContainer !== undefined && imagesLoaded === 0) {
				// Delay to understand page navigation
				setTimeout(() => {
					const position = offset(el).top;
					scrollTo(400, position);
					if (!window.location.hash) {
						window.history.replaceState({}, '', window.location.pathname + hash);
					}
				}, 300);
			} else {
				setTimeout(allLoaded, 50);
			}
		};

		allLoaded();
	}

	handleLoadMore = (automatic = false) => {
		const { pagination, list, cache, currentPath, paging = false, paginationLoader = false, limit = this.props.setup.statics.productLimit } = this.props;
		const currentPage = pagination[currentPath] || 1;
		const pageBase = list || cache;
		const maxPages = paging ? Math.ceil(paging.total / paging.limit) : Math.ceil(pageBase.length / limit);

		if ((automatic !== true && currentPage === 3 && currentPage < maxPages) || (currentPage !== 3 && currentPage < maxPages)) {
			// 3rd page human must click button
			this.props.updatePagination(currentPath, currentPage + 1);
			if (paginationLoader !== false) paginationLoader(currentPage + 1);
		}
	}

	toggleMinorProducts = (e) => {
		const currentSection = e.currentTarget.dataset.id;

		this.setState((prevState) => {
			const i = prevState.minorSectionExpanded.indexOf(currentSection);
			return {
				minorSectionExpanded: i === -1 ? [...prevState.minorSectionExpanded, currentSection] : [...prevState.minorSectionExpanded.slice(0, i), ...prevState.minorSectionExpanded.slice(i + 1)],
			};
		});
	}

	toggleProductItem = (index, expandedDirection = 'left') => {
		this.setState((prevState) => {
			const productExpandedIndex = prevState.productItemExpanded.indexOf(index);
			if (window.innerWidth < 600 && prevState.productItemExpanded.length > 0 && productExpandedIndex === -1) {
				setTimeout(() => {
					this.setState({ productItemExpanded: [index] });
				}, 350);

				return { expandedDirection, productItemExpanded: [] };
			}
			if (window.innerWidth < 600 && productExpandedIndex !== -1) return { expandedDirection, productItemExpanded: [] };
			if (window.innerWidth < 600 && productExpandedIndex === -1) return { expandedDirection, productItemExpanded: [index] };

			if (productExpandedIndex === -1) return { expandedDirection, productItemExpanded: [...prevState.productItemExpanded, index] };

			// remove item from state
			return { expandedDirection, productItemExpanded: [...prevState.productItemExpanded.slice(0, productExpandedIndex), ...prevState.productItemExpanded.slice(productExpandedIndex + 1)] };
		});
	}

	render() {
		const {
			ListComponent,
			setup,
			list,
			cache,
			currentPath,
			baseUri,
			cmsSections = {},
			isFiltered = false,
			canonical = false,
			productPicker = null,
		} = this.props;

		const { expandedDirection, minorSectionExpanded, productItemExpanded } = this.state;
		const pageBase = list || cache;
		let collected = {};

		// Highest category + together all under categories to enable duplicate products.
		/*
		collected = Object.keys(categories).reduce((acc, category) => {
			const categoryKey = category;
			if (category.indexOf(baseUri) !== 0 || categoryKey.indexOf(currentPath) !== 0 || baseUri === category) return acc;
			let sectionProducts = categories[category];

			// Remove duplicated products from subcategories.
			sectionProducts = Object.keys(categories).reduce((accDiff, categoryDiff) => {
				if (categoryDiff.indexOf('/') === -1 || categoryDiff.indexOf(category) !== 0 || category === categoryDiff) return accDiff;
				return [...difference(accDiff, categories[categoryDiff])];
			}, sectionProducts);

			sectionProducts = sectionProducts.filter((rbp) => cache[rbp] !== undefined).map((rbp) => cache[rbp]);
			return { ...acc, [categoryKey]: sectionProducts };
		}, {});
		*/

		if (productPicker) {
			collected = {
				unsorted: [...pageBase.map((item) => cache[item])],
			};
		} else {
			const sorted = pageBase.reduce((acc, item) => {
				const baseUrl = rootUrl(cache[item].canonicalUri);
				if (!acc[baseUrl]) acc[baseUrl] = [cache[item]];
				else acc[baseUrl].push(deepClone(cache[item]));
				return acc;
			}, {});
			collected[baseUri] = Object.keys(sorted).reduce((acc, items) => ([...acc, ...sorted[items]]), []);
		}

		return (
			<div className={[s.paginationContainer, (pageBase.length < 4 ? s.paginationContainer__centered : null)].join(' ')} ref={this.paginationContainer}>
				{
					Object.keys(collected).map((collection) => {
						const minorCategory = (collection.match(/\//g) || []).length > 1;
						let items = collected[collection].filter((item) => !list || (list && cache[item.groupId]));
						const collectionSplit = currentPath.split('/').length > 1 ? collection.split('/').slice(2) : collection.split('/').slice(1);
						const collectionKey = collectionSplit.join('-');
						const collectionCms = collectionSplit.join('/') || 'default';
						const topCollectionSections = [];
						const bottomCollectionSections = [];

						if (cmsSections?.[collectionCms]?.parts) {
							cmsSections[collectionCms].parts.forEach((part) => {
								if (!part?.sections) return;
								const insertPosition = parseInt(part.partsMeta.priority, 10) || 0;

								if (insertPosition === 0) {
									topCollectionSections.push(part.sections);
								} else if (insertPosition < 0) {
									bottomCollectionSections[Math.abs(insertPosition)] = part.sections;
								} else if (!isFiltered) {
									items = [...items.slice(0, insertPosition - 1), part, ...items.slice(insertPosition - 1)];
								}
							});
						}

						return (
							<Fragment key={collection}>
								{
									topCollectionSections.length > 0 ? (
										<div id={urlify(collectionKey)} className={s.paginationContainer_section_cms}>
											{
												topCollectionSections.map((topSection) => (
													<TemplateParser key={Object.keys(topSection).join('-')} sections={topSection} setup={setup} />
												))
											}
										</div>
									) : <div id={urlify(collectionKey)} />
								}
								<section className={[s.paginationContainer_section, (productPicker ? s.paginationContainer_section__productPickerScroll : null), (minorCategory ? s.paginationContainer_section__minorCategory : null), (productPicker !== null ? s.paginationContainer_section__productPicker : null), (minorSectionExpanded.indexOf(collectionKey) !== -1 ? s.paginationContainer_section__expanded : null)].join(' ')}>
									<div className={[s.paginationContainer_section_items, (items.length < 3 ? s.paginationContainer_section_items__centered : null)].join(' ')}>
										{
											items.map((item, i) => {
												const id = item.groupId;
												const component = [];
												if (item?.partsMeta) {
													component.push(<div key={item?.meta?.id} className={[s.paginationContainer_section_items_media, 'u_column', `u_widthTight__${item.partsMeta.width || 4}`].join(' ')}><TemplateParser sections={item.sections} setup={setup} inline skipFirst /></div>);
												}
												if (item?.uri) {
													component.push(<ListComponent key={list ? id : urlify(translate(setup, item.uri))} id={id} content={item} canonical={canonical} setup={setup} currentPath={currentPath} productPicker={productPicker !== null} variation={minorCategory ? 'minorProduct' : ''} currentView={collection} itemIndex={i} toggleProductItem={this.toggleProductItem} expandedDirection={expandedDirection} productItemExpanded={productItemExpanded} productLayout={this.props.productLayout} />);
												}
												return component;
											})
										}
									</div>
									{
										minorCategory && (
											<div className={[s.paginationContainer_section_loadAccessories, items.length < 6 ? s.paginationContainer_section_loadAccessories__few : null].join(' ')}>
												<Button type="button" clickEventHandler={this.toggleMinorProducts} data-id={urlify(collectionKey)}>{ minorSectionExpanded.indexOf(collectionKey) === -1 ? translate(setup, 'seeAllAccessories') : translate(setup, 'hideAccessories') }</Button>
											</div>
										)
									}
								</section>
								{
									bottomCollectionSections.length > 0 && (
										<div className={s.paginationContainer_section_cms}>
											{
												bottomCollectionSections.map((bottomSection) => (
													<Fragment key={Object.keys(bottomSection).join('-')}>
														<TemplateParser sections={bottomSection} setup={setup} skipFirst />
													</Fragment>
												))
											}
										</div>
									)
								}
							</Fragment>
						);
					})
				}
			</div>
		);
	}
}

const mapState = ({ environment, location }, { showAll = false, paging = null, canonical = false, magazine = false, ...props }) => ({
	pagination: environment.pagination,
	payload: location.payload,
	setup: environment.setup,
	showAll,
	paging,
	canonical,
	magazine,
	...props,
});

export default hot(module)(connect(mapState, { updatePagination })(Pagination));
